import React from 'react';
import { HomeAudiences } from '../components/home/HomeAudiences';
import { ConsultantContactList } from '../components/consultants/ConsultantContactList';
import { ProcessTimeline } from '../components/sections/ProcessTimeline';
import { ArchitecturalForm } from '../components/ui/ArchitecturalForm';
import { ButtonLink } from '../components/ui/ButtonLink';
import { PageHero } from '../components/ui/PageHero';
import { Reveal } from '../components/ui/Reveal';
import { SectionHeading } from '../components/ui/SectionHeading';
import { values } from '../data/firm';
import { images } from '../data/images';
import { usePageMeta } from '../hooks/usePageMeta';
const approach = [{
  title: 'Our executive search philosophy',
  body: ['We believe the best appointments come from understanding an organization deeply before looking outward. Titles and CVs tell part of the story; strategy, culture, and the specific challenges a leader will face tell the rest.', 'Our role is to bring structure, judgment, and candour to a process that matters enormously to everyone involved.']
}, {
  title: 'Our approach to client partnerships',
  body: ['We work as an extension of your leadership team. That means agreeing a clear brief, sharing honest feedback from the market, and keeping you informed at every stage — including when the market suggests the brief should evolve.']
}, {
  title: 'Our approach to candidate engagement',
  body: ['Experienced leaders deserve a considered, respectful experience. We explain opportunities clearly, listen to what candidates want from their next role, and give honest feedback — whether or not an opportunity progresses.']
}, {
  title: 'Professional discretion and confidentiality',
  body: ['Executive appointments often involve sensitive information on both sides. We share client and candidate information only with consent and only as each stage requires, and we never present a candidate to an organization without first discussing it with them.']
}];
export function About() {
  usePageMeta('About Us', 'Learn about Aurevane Search Partners — our mission, values, and relationship-driven approach to executive search, leadership recruitment, and confidential search.');
  return <>
      <PageHero label="About Us" title="Built Around People. Focused on Leadership." intro="Aurevane Search Partners is an executive search and leadership advisory firm. We help organizations appoint the leaders who will shape their future, and help experienced professionals find roles worthy of their ambitions." visual={<ArchitecturalForm variant="stack" tone="dark" data-id="element-500" data-mp="2llf9p3gj7yt6" />} data-id="element-499" data-mp="22ncfma94nboi" />

      <section className="bg-porcelain py-24 lg:py-32" data-id="element-501" data-mp="2lijazcwv163o">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-12 lg:px-10" data-id="element-502" data-mp="m8ul0739pfvg">
          <Reveal className="lg:col-span-6" data-id="element-503" data-mp="r7xkzd4e4vwk">
            <SectionHeading label="Who we are" title="An executive search firm grounded in understanding" data-id="element-504" data-mp="r8pcocuva9hk" />
            <div className="mt-8 max-w-prose space-y-5 text-lg leading-relaxed text-plum/75" data-id="element-505" data-mp="ewn2g6u0jmlp">
              <p data-id="element-506" data-mp="9xjtibawww8g">
                We focus on senior appointments — C-suite executives, board directors, and senior leaders — where the right decision has lasting consequences for an organization and for the individual who takes on the role.
              </p>
              <p data-id="element-507" data-mp="12lpg73lj0vax">
                Our work combines structured research with genuine relationships. We take time to understand the organizations we partner with and the leaders we speak with, so that every introduction is made for a reason.
              </p>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={0.05} data-id="element-508" data-mp="olofbo59hnvg">
            <img src={images.aboutEditorial} alt="A refined meeting room with a long oak table, cream walls, and a copper pendant light" loading="lazy" className="aspect-[3/2] w-full rounded-2xl object-cover" data-id="element-509" data-mp="3de0ruipbhot9" />
          </Reveal>
        </div>
      </section>

      <section className="on-dark bg-aubergine text-porcelain" data-id="element-510" data-mp="15vqz3lh46a6p">
        <div className="mx-auto grid max-w-7xl divide-y divide-porcelain/10 px-6 lg:grid-cols-2 lg:divide-x lg:divide-y-0 lg:px-10" data-id="element-511" data-mp="r62ykmgrbfk1">
          <div className="py-16 lg:py-24 lg:pr-16" data-id="element-512" data-mp="w5xnikgz1v2g">
            <h2 className="font-display text-sm font-semibold text-copper-light" data-id="element-513" data-mp="w2jpfofcc80t">Our mission</h2>
            <p className="mt-5 font-serif text-3xl leading-snug sm:text-4xl" data-id="element-514" data-mp="1jsnr3q8yxxp6">
              To connect organizations with leaders whose experience, judgment, and values fit the moment they are in.
            </p>
          </div>
          <div className="py-16 lg:py-24 lg:pl-16" data-id="element-515" data-mp="1euui6pga6pl7">
            <h2 className="font-display text-sm font-semibold text-copper-light" data-id="element-516" data-mp="1exgratitld5j">Our vision</h2>
            <p className="mt-5 font-serif text-3xl leading-snug sm:text-4xl" data-id="element-517" data-mp="3viq0959wtrj0">
              To be a trusted, discreet partner for leadership decisions — valued for the quality of our thinking and the strength of our relationships.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-porcelain py-24 lg:py-32" data-id="element-518" data-mp="3n2hcvokkszsd">
        <div className="mx-auto max-w-7xl px-6 lg:px-10" data-id="element-519" data-mp="5vf48gus95la">
          <SectionHeading label="Core values" title="What guides our work" data-id="element-520" data-mp="5ssvwf02im20" />
          <ul className="mt-16 grid gap-x-12 gap-y-12 md:grid-cols-2 lg:grid-cols-3" data-id="element-521" data-mp="wbxmrz5mrb7">
            {values.map(v => <li key={v.title} className="border-t border-copper/40 pt-6" data-id="element-522" data-mp="399hrx720yddf">
                <h3 className="font-serif text-2xl font-medium text-aubergine" data-id="element-523" data-mp="34e2kawum1dpg">{v.title}</h3>
                <p className="mt-3 leading-relaxed text-plum/70" data-id="element-524" data-mp="3j2youjxgk8lb">{v.description}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-limestone py-24 lg:py-32" data-id="element-525" data-mp="2qkkzz9u3nwyd">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10" data-id="element-526" data-mp="2qj9th299sb3k">
          <div className="lg:col-span-4" data-id="element-527" data-mp="2lli3ids6qz20">
            <div className="lg:sticky lg:top-32" data-id="element-528" data-mp="3baf5nwly12tx">
              <SectionHeading label="How we work" title="Partnership, candour, and discretion" data-id="element-529" data-mp="3b9nklfpz2nhw" />
            </div>
          </div>
          <div className="divide-y divide-copper/30 lg:col-span-7 lg:col-start-6" data-id="element-530" data-mp="3f9e6b50n6dfk">
            {approach.map(block => <article key={block.title} className="py-10 first:pt-0 last:pb-0" data-id="element-531" data-mp="3p1afbb49nnyk">
                <h3 className="text-2xl font-semibold text-aubergine" data-id="element-532" data-mp="2sbayyuhvjl5f">{block.title}</h3>
                <div className="mt-4 space-y-4" data-id="element-533" data-mp="2sdnq9y9iv7v5">
                  {block.body.map(p => <p key={p} className="max-w-prose leading-relaxed text-plum/75" data-id="element-534" data-mp="2wez99kxw4uud">
                      {p}
                    </p>)}
                </div>
              </article>)}
          </div>
        </div>
      </section>

      <section className="bg-porcelain py-24 lg:py-32" data-id="element-535" data-mp="gkp6bubmwuvg">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-12 lg:px-10" data-id="element-536" data-mp="lkv1vll0z1it">
          <div className="lg:col-span-5" data-id="element-537" data-mp="lmo9p47djrse">
            <SectionHeading label="Our search methodology" title="Seven stages, one clear standard" intro="Every search follows a consistent structure, adapted to the seniority, sensitivity, and market of each appointment." data-id="element-538" data-mp="2635l1e6isrki" />
            <ArchitecturalForm variant="frame" tone="light" className="mt-8 hidden max-w-xs lg:block" data-id="element-539" data-mp="1re9c7ewn3aiu" />
          </div>
          <div className="lg:col-span-6 lg:col-start-7" data-id="element-540" data-mp="1nco4s92vjog1">
            <ProcessTimeline data-id="element-541" data-mp="1na1o9jy09ylo" />
          </div>
        </div>
      </section>

      <section className="border-t border-plum/10 bg-porcelain py-24 lg:py-28" data-id="element-542" data-mp="1ic04ewsr9v95">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-12 lg:px-10" data-id="element-543" data-mp="1dezx6ik66mgg">
          <div className="lg:col-span-4" data-id="element-544" data-mp="18g6vdne8wjw4">
            <SectionHeading label="Our team" title="Meet the consultants" intro="Our Executive Search Consultants are your direct point of contact for every search." data-id="element-545" data-mp="18h88ryiqhih0" />
            <ButtonLink to="/consultants" variant="outline" arrow className="mt-8" data-id="element-546" data-mp="32ug7405x4c7e">
              View all profiles
            </ButtonLink>
          </div>
          <div className="lg:col-span-7 lg:col-start-6" data-id="element-547" data-mp="2gmm3x547as9w">
            <ConsultantContactList columns={1} data-id="element-548" data-mp="2gmdmr111p0sw" />
          </div>
        </div>
      </section>

      <HomeAudiences data-id="element-549" data-mp="22uz6h1yivdpj" />
    </>;
}